import * as React from "react";
import { connect } from "react-redux";
import { bindActionCreators, Dispatch } from "redux";
import * as _ from "lodash";
import Editor from "../../components/Editing/Editor";
import TextField from "../../components/Editing/TextField";
import { nameofFactory, hasWriteAccess } from "../../utils/utils";
import { EditActions, editActions } from "../../actions/editActions";
import NumberField from "../../components/Editing/NumberField";
import DropdownField from "../../components/Editing/DropdownField";

type StateProps = {
  editedEnumeration: Api.ioOption | Api.ioDataType;
  storedEnumeration: Api.ioOption | Api.ioDataType;
  pdApi: State.PdApi;
  activeUser: Api.User | undefined;
};

type DispatchProps = {
  editActions: typeof editActions;
};

type OwnProps = {
  toggleModal: () => void;
  dataFieldName: keyof State.PdApi;
};

const ioDatatypeFields = nameofFactory<Api.ioDataType>();

export type EditIoDatatypeProps = StateProps & DispatchProps & OwnProps;

export class EditIoDatatype extends React.Component<EditIoDatatypeProps> {
  render(): JSX.Element {
    const {
      editedEnumeration,
      storedEnumeration,
      activeUser,
      toggleModal,
      dataFieldName,
      pdApi
    } = this.props;
    const isNew = !storedEnumeration;
    const permission = hasWriteAccess(activeUser, "Lift");
    const enumOption = pdApi.ioOptionsEnumType.map(item => ({
      key: item.enumID,
      text: item.enumTypeName,
      value: item.enumID
    }));
    return (
      <Editor
        itemName={"Enumeration"}
        originalObject={storedEnumeration}
        editedObject={editedEnumeration}
        hasWritePermission={hasWriteAccess(activeUser, "Lift")}
        isValid={this.isAllFieldsValid()}
        cancel={this.props.editActions.cancelEditing}
        update={() => this.props.editActions.update(dataFieldName as Api.OptionsGrud)}
        create={() => this.props.editActions.add(dataFieldName as Api.OptionsGrud)}
        remove={{ callback: toggleModal }}
      >
        <NumberField
          id="value"
          label="Value"
          value={editedEnumeration.value ? editedEnumeration.value : 0}
          isError={!this.isFieldValid(ioDatatypeFields("value"))}
          errorMessage="The value must be unique."
          readonly={!isNew}
          onChange={value =>
            this.props.editActions.setEdited({ ...editedEnumeration, value: value })
          }
        />
        {dataFieldName !== "ioDataTypes" && (
          <DropdownField
            id="Options EnumType"
            label="Options EnumType"
            options={enumOption}
            value={(editedEnumeration as Api.ioOption).enumTypeID}
            onChange={value => {
              this.props.editActions.setEdited({ ...editedEnumeration, enumTypeID: value });
            }}
          />
        )}
        <TextField
          id="enumName"
          label="Enumeration Name"
          value={editedEnumeration.enumName}
          isError={!this.isFieldValid(ioDatatypeFields("enumName"))}
          errorMessage={
            !this.isEmptyString(editedEnumeration.enumName)
              ? "The enumeration name must be unique."
              : ""
          }
          onChange={enumName =>
            this.props.editActions.setEdited({ ...editedEnumeration, enumName: enumName.trim() })
          }
          readonly={!permission}
        />
        <TextField
          id="comment"
          label="Comment"
          value={editedEnumeration.comment}
          isError={!this.isFieldValid(ioDatatypeFields("comment"))}
          onChange={comment =>
            this.props.editActions.setEdited({ ...editedEnumeration, comment: comment.trim() })
          }
          readonly={!permission}
        />
      </Editor>
    );
  }

  private isEmptyString = (str: string): boolean => !(!!str && str.replace(/\s/g, "").length > 0);

  private isFieldValid = (field: string): boolean => {
    const { editedEnumeration, pdApi, dataFieldName } = this.props;
    if (!Number.isInteger(editedEnumeration.value)) return false;
    if (dataFieldName === "ioOptions" && field === ioDatatypeFields("value")) {
      return pdApi.ioOptions
        .filter(option => option.enumTypeID === (editedEnumeration as Api.ioOption).enumTypeID)
        .find(
          enumOption =>
            enumOption.value === editedEnumeration.value && enumOption.id !== editedEnumeration.id
        )
        ? false
        : true;
    }
    switch (field) {
      case ioDatatypeFields("enumName"): {
        const { enumName } = editedEnumeration;
        if (!enumName || enumName.replace(/\s/g, "").length === 0) {
          return false;
        } else {
          return _.values(pdApi[this.props.dataFieldName]).find(
            enumeration =>
              enumeration.id !== editedEnumeration.id &&
              enumeration.enumName.trim() === editedEnumeration.enumName.trim()
          )
            ? false
            : true;
        }
      }
      case ioDatatypeFields("value"): {
        if (this.props.storedEnumeration) {
          return true;
        }
        return _.values(pdApi[this.props.dataFieldName]).find(
          enumeration =>
            enumeration.id !== editedEnumeration.id && enumeration.value === editedEnumeration.value
        )
          ? false
          : true;
      }
      default:
        return true;
    }
  };

  private isAllFieldsValid = (): boolean => {
    const { editedEnumeration } = this.props;
    const temp = _.keys(editedEnumeration);
    for (const key of temp) {
      if (!this.isFieldValid(key)) {
        return false;
      }
    }
    return true;
  };
}

const mapStateToProps = (state: State.Root) => ({
  editedEnumeration: state.editView.editedObject,
  storedEnumeration: state.editView.storedObject,
  pdApi: state.pdApi,
  activeUser: state.main.activeUser
});

const mapDispatchToProps = (dispatch: Dispatch<EditActions>) => ({
  editActions: bindActionCreators({ ...editActions }, dispatch)
});

export default connect(mapStateToProps, mapDispatchToProps)(EditIoDatatype);


